import { useCallback } from "react";
import type { CoverageGap } from "@shared/types/corpus/api.ts";
import { createCoverageGap, getCoverageGaps } from "../../../../lib/corpusApi";
import { useAsyncPaginatedData } from "../../../../hooks/useAsyncPaginatedData";
import { PAGE_SIZE_OPTIONS, type ListPagination } from "../../../../lib/pagination";
import { useUrlFilters } from "../../../../lib/url_filters";
import { coverageGapFiltersSchema, type CoverageGapFilters } from "./coverageGapFilters";

const GAPS_PAGE_SIZE = 50;

export interface UseCoverageGapsResult {
  filters: CoverageGapFilters;
  setFilters: (filters: CoverageGapFilters) => void;
  /** The open coverage gaps ranked by `filters.sort`, or the closed ones, by `filters.state`. */
  gaps: {
    items: CoverageGap[];
    loading: boolean;
    error: string | null;
    pagination: ListPagination;
  };
  createGap: (name: string, note: string) => Promise<CoverageGap>;
}

/** The Coverage gaps list: the gaps ranked by demand, open or closed. */
export function useCoverageGaps(): UseCoverageGapsResult {
  const [filters, setFilters] = useUrlFilters(coverageGapFiltersSchema);

  const gapsData = useAsyncPaginatedData(
    (page) =>
      getCoverageGaps({
        sort: filters.sort,
        state: filters.state,
        toTell: filters.toTell,
        ...page,
      }),
    { pageSize: GAPS_PAGE_SIZE, key: `${filters.state}:${filters.sort}:${filters.toTell}` }
  );
  const { refresh } = gapsData;

  const createGap = useCallback(
    async (name: string, note: string) => {
      const gap = await createCoverageGap({ name, note });
      await refresh();
      return gap;
    },
    [refresh]
  );

  return {
    filters,
    setFilters,
    gaps: {
      items: gapsData.items,
      loading: gapsData.loading,
      error: gapsData.error,
      pagination: { ...gapsData.pagination, pageSizeOptions: PAGE_SIZE_OPTIONS },
    },
    createGap,
  };
}
